<script setup lang="ts">
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import NewTreeComponent from '@/components/createdUi/NewTreeComponent.vue'
import { fetchTicketCategories, omitTreeIds, searchDepartments } from '@/components/createdUi/treeSearch'
import SearchableSelectList, { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { parseFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { TICKETS_ANCHORS } from '@/modules/onboarding/scenarios/tickets/anchors'
import { refDebounced } from '@vueuse/core'
import { computed, provide, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useTicketSettingsStore } from '../../stores/ticketSettingsStore'
import type {
  ITicketCategoryResponsibleEmployee,
  ITicketCategoryTreeNode,
  ITicketWorkGroupPerformer,
} from '../../types/settings/ITicketCategorySettings'
import TicketCategoryTreeNode from './TicketCategoryTreeNode.vue'

/**
 * Пропсы модального окна управления категориями заявок
 */
const props = withDefaults(
  defineProps<{
    /** Управление видимостью модального окна */
    modelValue: boolean
    /** Заголовок модального окна */
    title?: string
  }>(),
  {
    title: 'Категории заявок',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { smAndDown } = useDisplay()
const ticketSettingsStore = useTicketSettingsStore()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()

// Хранение данных
const searchQuery = ref('')
const searchDebounced = refDebounced(searchQuery, 300)

const selectedNode = ref<ITicketCategoryTreeNode | null>(null)
const isSidebarOpen = ref(false)
const isCreateMode = ref(false)

/** Категория из ДаоМед — в форме только is_active, DnD запрещён */
const isDaomedMain = computed(() => !isCreateMode.value && !!selectedNode.value?.is_daomed_main)

/** Фильтр дерева: all | custom | expert (значение expert = глобальные) */
const treeFilterOptions = [
  { value: 'all', label: 'Все' },
  { value: 'custom', label: 'Свои' },
  { value: 'expert', label: 'Глобальные' },
]
const treeFilterMode = computed({
  get: () => ticketSettingsStore.categoryTreeFilterMode,
  set: (value: string) => { ticketSettingsStore.categoryTreeFilterMode = value },
})
const isGlobalTreeFilter = computed(() => treeFilterMode.value === 'expert')

// Состояние компактного вида (localStorage)
const isCompactModeActive = ref(localStorage.getItem('ticket_cat_tree_compact_mode') === 'true')
watch(isCompactModeActive, newVal => {
  localStorage.setItem('ticket_cat_tree_compact_mode', String(newVal))
})

// Состояние скрытия иерархии (localStorage)
const isHideHierarchyActive = ref(localStorage.getItem('ticket_cat_tree_hide_hierarchy') === 'true')
const collapsedNodes = ref<Array<string>>([])

function getAllNodeIds(nodes: ITicketCategoryTreeNode[]): Array<string> {
  let ids: Array<string> = []
  for (const n of nodes) {
    ids.push(n.id)
    if (n.children && n.children.length) {
      ids = ids.concat(getAllNodeIds(n.children))
    }
  }
  return ids
}

function syncCollapsedAfterReload() {
  if (isHideHierarchyActive.value)
    collapsedNodes.value = getAllNodeIds(ticketSettingsStore.categoriesTree)
}

watch(isHideHierarchyActive, newVal => {
  localStorage.setItem('ticket_cat_tree_hide_hierarchy', String(newVal))
  if (newVal) {
    collapsedNodes.value = getAllNodeIds(ticketSettingsStore.categoriesTree)
  } else {
    collapsedNodes.value = []
  }
})

// Состояние drag & drop перетаскивания (localStorage)
const isDragDropEnabled = ref(localStorage.getItem('ticket_cat_tree_drag_drop') === 'true')
watch(isDragDropEnabled, newVal => {
  localStorage.setItem('ticket_cat_tree_drag_drop', String(newVal))
})

watch(isGlobalTreeFilter, isGlobal => {
  if (isGlobal) isDragDropEnabled.value = false
})

provide('isCompactModeActive', isCompactModeActive)
provide('isDragDropEnabled', isDragDropEnabled)
provide('searchQuery', searchQuery)
provide('onToggleNodeActive', (node: ITicketCategoryTreeNode, isActive: boolean) => {
  void ticketSettingsStore.toggleCategoryActive(node.id, isActive).then(ok => {
    if (ok && selectedNode.value && String(selectedNode.value.id) === String(node.id))
      localIsActive.value = isActive
  })
})

function onTreeFilterChanged(value: string | null) {
  if (!value) return
  void ticketSettingsStore.setCategoryTreeFilter(value, searchDebounced.value).then(() => {
    syncCollapsedAfterReload()
  })
}

// Drag and Drop логика
const draggedNodeId = ref<string | null>(null)
const dragOverNodeId = ref<string | null>(null)
const draggedNode = ref<ITicketCategoryTreeNode | null>(null)
const disallowedTargetIds = ref<Set<string>>(new Set())

function getDescendantIds(node: ITicketCategoryTreeNode): Set<string> {
  const ids = new Set<string>()
  const collect = (n: ITicketCategoryTreeNode) => {
    if (n.children) {
      for (const child of n.children) {
        ids.add(child.id)
        ids.add(String(child.id))
        collect(child)
      }
    }
  }
  collect(node)
  return ids
}

function findNodeInTreeById(nodes: ITicketCategoryTreeNode[], id: string): ITicketCategoryTreeNode | null {
  for (const node of nodes) {
    if (String(node.id) === String(id)) {
      return node
    }
    if (node.children && node.children.length > 0) {
      const found = findNodeInTreeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

function isAllowedDrop(targetId: string): boolean {
  if (!draggedNodeId.value) return false
  if (draggedNode.value?.is_daomed_main) return false
  if (targetId !== 'root-virtual') {
    const target = findNodeInTreeById(ticketSettingsStore.categoriesTree, targetId)
    if (target?.is_daomed_main) return false
  }
  return !disallowedTargetIds.value.has(targetId)
}

function removeNodeFromTree(nodes: ITicketCategoryTreeNode[], id: string): ITicketCategoryTreeNode | null {
  for (let i = 0; i < nodes.length; i++) {
    if (String(nodes[i].id) === String(id)) {
      return nodes.splice(i, 1)[0]
    }
    const children = nodes[i].children
    if (children && children.length > 0) {
      const removed = removeNodeFromTree(children, id)
      if (removed) return removed
    }
  }
  return null
}

async function moveNode(draggedId: string, targetId: string) {
  const draggedNodeVal = removeNodeFromTree(ticketSettingsStore.categoriesTree, draggedId)
  if (!draggedNodeVal) return

  if (targetId === 'root-virtual') {
    ticketSettingsStore.categoriesTree.push(draggedNodeVal)
  } else {
    const targetNode = findNodeInTreeById(ticketSettingsStore.categoriesTree, targetId)
    if (targetNode) {
      if (!targetNode.children) {
        targetNode.children = []
      }
      targetNode.children.push(draggedNodeVal)
    }
  }

  try {
    const parentId = targetId === 'root-virtual' ? null : String(targetId)
    await ticketSettingsStore.updateCategory(String(draggedId), { parent_id: parentId })
    feedbackStore.showToast({
      type: 'success',
      message: 'Категория успешно перемещена',
      time: 4000,
    })
  } catch (error) {
    console.error('❌ Ошибка при перемещении категории:', error)
    await ticketSettingsStore.fetchCategoriesTree()
  }
}

provide('draggedNodeId', draggedNodeId)
provide('dragOverNodeId', dragOverNodeId)
provide('draggedNode', draggedNode)

provide('onNodeDragStart', (node: ITicketCategoryTreeNode) => {
  if (node.is_daomed_main) return
  draggedNodeId.value = node.id
  draggedNode.value = node

  const disallowed = getDescendantIds(node)
  disallowed.add(node.id)
  disallowed.add('root-virtual')
  disallowedTargetIds.value = disallowed
})

provide('onNodeDragOver', (node: ITicketCategoryTreeNode) => {
  if (isAllowedDrop(node.id)) {
    dragOverNodeId.value = node.id
  } else {
    dragOverNodeId.value = null
  }
})

provide('onNodeDragLeave', () => {
  dragOverNodeId.value = null
})

provide('onNodeDrop', (targetNode: ITicketCategoryTreeNode) => {
  if (draggedNodeId.value && isAllowedDrop(targetNode.id)) {
    moveNode(draggedNodeId.value, targetNode.id)
  }
  draggedNodeId.value = null
  draggedNode.value = null
  dragOverNodeId.value = null
  disallowedTargetIds.value.clear()
})

provide('onNodeDragEnd', () => {
  draggedNodeId.value = null
  draggedNode.value = null
  dragOverNodeId.value = null
  disallowedTargetIds.value.clear()
})

function onContainerDrop(e: DragEvent) {
  const target = e.target as HTMLElement
  if (target.closest('.dept-node-card')) {
    return
  }
  if (draggedNodeId.value) {
    moveNode(draggedNodeId.value, 'root-virtual')
  }
  draggedNodeId.value = null
  draggedNode.value = null
  dragOverNodeId.value = null
  disallowedTargetIds.value.clear()
}

// Поля формы
const localName = ref('')
const localDescription = ref('')
const localParentId = ref<string | null>(null)
const localServiceId = ref<string | null>(null)
const localWorkGroupId = ref<number | null>(null)
const localIsActive = ref(true)
const localResponsible = ref<ITicketCategoryResponsibleEmployee | null>(null)
const showWorkGroupMenu = ref(false)

function formatPerformerItem(p: ITicketWorkGroupPerformer) {
  const name = [p.last_name, p.first_name, p.middle_name].filter(Boolean).join(' ')
    || [p.first_name, p.last_name].filter(Boolean).join(' ')
    || 'Сотрудник'
  const descParts = [p.position_name, p.department_name].filter(Boolean)
  return {
    id: p.id,
    name,
    avatar: p.avatar || undefined,
    description: descParts.join(' • '),
  }
}

const selectedWorkGroup = computed(() => {
  if (!localWorkGroupId.value) return null
  return (ticketSettingsStore.workGroups || []).find(g => g.id === localWorkGroupId.value) || null
})

const selectedWorkGroupName = computed(() => {
  return selectedWorkGroup.value ? selectedWorkGroup.value.name : ''
})

const selectedWorkGroupPerformers = computed(() => {
  if (!selectedWorkGroup.value?.performers?.length) return []
  return selectedWorkGroup.value.performers.map(formatPerformerItem)
})

const workGroupSelectItems = computed<ISearchableSelectItem[]>(() => {
  return (ticketSettingsStore.workGroups || []).map(wg => ({
    id: wg.id,
    label: wg.name,
    performersCount: wg.performers?.length ?? wg.performers_count ?? 0,
    formattedPerformers: (wg.performers || []).map(formatPerformerItem),
    raw: wg,
  }))
})

const workGroupSearchQuery = ref('')

async function onSearchWorkGroups(query: string) {
  workGroupSearchQuery.value = query
  await ticketSettingsStore.fetchWorkGroups(query)
}

async function onLoadMoreWorkGroups() {
  if (ticketSettingsStore.workGroupsNextCursor && !feedbackStore.isGlobalLoading) {
    await ticketSettingsStore.fetchWorkGroups(workGroupSearchQuery.value, ticketSettingsStore.workGroupsNextCursor)
  }
}

const onSelectWorkGroup = (item: ISearchableSelectItem) => {
  localWorkGroupId.value = Number(item.id)
  showWorkGroupMenu.value = false
}

const onClearWorkGroup = () => {
  localWorkGroupId.value = null
  workGroupSearchQuery.value = ''
  ticketSettingsStore.fetchWorkGroups()
}

const showValidationErrors = ref(false)
const isConfirmDeleteOpen = ref(false)

// Zoom & Pan
const zoom = ref(100)
const zoomInput = ref(String(zoom.value))
const treeContainerOuter = ref<HTMLElement | null>(null)

let isPanning = false
let startPanX = 0
let startPanY = 0
let startScrollLeft = 0
let startScrollTop = 0

function startDrag(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (
    target.closest('.dept-node-card')
    || target.closest('.zoom-control-panel')
    || target.closest('.v-btn')
    || target.closest('input')
  ) {
    return
  }

  isPanning = true
  startPanX = e.clientX
  startPanY = e.clientY
  if (treeContainerOuter.value) {
    startScrollLeft = treeContainerOuter.value.scrollLeft
    startScrollTop = treeContainerOuter.value.scrollTop
  }
}

function onDrag(e: MouseEvent) {
  if (!isPanning || !treeContainerOuter.value) return
  e.preventDefault()
  const dx = e.clientX - startPanX
  const dy = e.clientY - startPanY
  treeContainerOuter.value.scrollLeft = startScrollLeft - dx
  treeContainerOuter.value.scrollTop = startScrollTop - dy
}

function stopDrag() {
  isPanning = false
}

function zoomIn() {
  if (zoom.value < 500) {
    zoom.value = Math.min(500, zoom.value + 10)
    zoomInput.value = String(zoom.value)
  }
}

function zoomOut() {
  if (zoom.value > 10) {
    zoom.value = Math.max(10, zoom.value - 10)
    zoomInput.value = String(zoom.value)
  }
}

function onZoomInputBlur() {
  const val = parseInt(zoomInput.value, 10)
  if (!isNaN(val)) {
    zoom.value = Math.max(10, Math.min(500, val))
  }
  zoomInput.value = String(zoom.value)
}

function toggleNodeCollapse(id: string) {
  const index = collapsedNodes.value.indexOf(id)
  if (index >= 0) {
    collapsedNodes.value.splice(index, 1)
  } else {
    collapsedNodes.value.push(id)
  }
}

watch(
  () => props.modelValue,
  isOpen => {
    if (isOpen) {
      collapsedNodes.value = isHideHierarchyActive.value
        ? getAllNodeIds(ticketSettingsStore.categoriesTree)
        : []
      void ticketSettingsStore.setCategoryTreeFilter('all', searchDebounced.value).then(() => {
        syncCollapsedAfterReload()
      })
    } else {
      closeModal()
    }
  },
  { immediate: true },
)

watch(searchDebounced, query => {
  if (props.modelValue) {
    void ticketSettingsStore.reloadCategoryTree(query).then(() => {
      syncCollapsedAfterReload()
    })
  }
})

async function loadFormDataDeps() {
  const promises: Promise<any>[] = []
  if (!ticketSettingsStore.workGroups?.length) {
    promises.push(ticketSettingsStore.fetchWorkGroups())
  }
  if (!apiStore.departmentsTree?.length) {
    promises.push(apiStore.fetchAllDepartments())
  }
  if (promises.length) {
    await Promise.all(promises)
  }
}

// Дерево категорий для селекта родителя (исключая текущую и её детей)
const parentCategoryTree = computed<ITreeNode[]>(() => {
  const forbiddenIds = selectedNode.value && !isCreateMode.value
    ? getDescendantIds(selectedNode.value)
    : new Set<string>()

  function transform(nodes: ITicketCategoryTreeNode[]): ITreeNode[] {
    return nodes
      .filter(n => !forbiddenIds.has(n.id) && !forbiddenIds.has(String(n.id)))
      .map(n => ({
        key: String(n.id),
        label: n.name,
        data: {
          name: n.name,
          selfId: String(n.id),
        },
        children: n.children && n.children.length > 0 ? transform(n.children) : [],
      }))
  }

  return transform(ticketSettingsStore.categoriesTree)
})

async function searchParentCategoryTree(query: string): Promise<ITreeNode[]> {
  const forbiddenIds = selectedNode.value && !isCreateMode.value
    ? getDescendantIds(selectedNode.value)
    : new Set<string>()
  const rows = await fetchTicketCategories(
    ticketSettingsStore.buildCategoryTreeParams(undefined, query),
  ) as ITicketCategoryTreeNode[]

  function transform(nodes: ITicketCategoryTreeNode[]): ITreeNode[] {
    return nodes.map(node => ({
      key: String(node.id),
      label: node.name,
      data: {
        name: node.name,
        selfId: String(node.id),
      },
      children: node.children?.length ? transform(node.children) : [],
    }))
  }

  return omitTreeIds(transform(rows), forbiddenIds)
}

const initialFormState = ref({
  name: '',
  description: '',
  parent_id: null as string | null,
  service_id: null as string | null,
  work_group_id: null as number | null,
  responsible_id: null as string | null,
  is_active: true,
})

const isChanged = computed(() => {
  if (isCreateMode.value) {
    const init = initialFormState.value
    return (
      localName.value.trim() !== '' ||
      localDescription.value.trim() !== '' ||
      localParentId.value !== init.parent_id ||
      localServiceId.value !== init.service_id ||
      localWorkGroupId.value !== init.work_group_id ||
      localResponsible.value !== null
    )
  }

  const init = initialFormState.value
  if (localName.value !== init.name) return true
  if (localDescription.value !== init.description) return true
  if (localParentId.value !== init.parent_id) return true
  if (localServiceId.value !== init.service_id) return true
  if (localWorkGroupId.value !== init.work_group_id) return true
  if ((localResponsible.value?.id ? String(localResponsible.value.id) : null) !== init.responsible_id) return true
  if (localIsActive.value !== init.is_active) return true

  return false
})

const isConfirmUnsavedChangesOpen = ref(false)
const pendingAction = ref<'close_modal' | 'close_sidebar' | null>(null)

function openCreateNode(parent?: ITicketCategoryTreeNode) {
  if (isGlobalTreeFilter.value) return

  isCreateMode.value = true
  selectedNode.value = {
    id: '',
    name: '',
    description: '',
    service: { id: '', name: '' },
    is_active: true,
    children: [],
  }

  localName.value = ''
  localDescription.value = ''
  localParentId.value = parent ? String(parent.id) : null
  localServiceId.value = parent?.service?.id ? String(parent.service.id) : null
  localWorkGroupId.value = null
  localIsActive.value = true
  localResponsible.value = null
  showValidationErrors.value = false

  initialFormState.value = {
    name: '',
    description: '',
    parent_id: parent ? String(parent.id) : null,
    service_id: parent?.service?.id ? String(parent.service.id) : null,
    work_group_id: null,
    responsible_id: null,
    is_active: true,
  }

  void loadFormDataDeps().then(() => {
    isSidebarOpen.value = true
  })
}

async function selectNode(node: ITicketCategoryTreeNode) {
  isCreateMode.value = false
  selectedNode.value = node

  localName.value = node.name || ''
  localDescription.value = node.description || ''
  localParentId.value = node.parent?.id ? String(node.parent.id) : (node.parent_id ? String(node.parent_id) : null)
  localServiceId.value = node.service?.id ? String(node.service.id) : (node.service_id ? String(node.service_id) : null)
  localWorkGroupId.value = node.work_group?.id || null
  localIsActive.value = node.is_active !== false
  localResponsible.value = node.responsible_employee ? { ...node.responsible_employee } : null
  showValidationErrors.value = false

  initialFormState.value = {
    name: node.name || '',
    description: node.description || '',
    parent_id: localParentId.value,
    service_id: localServiceId.value,
    work_group_id: node.work_group?.id || null,
    responsible_id: node.responsible_employee?.id ? String(node.responsible_employee.id) : null,
    is_active: node.is_active !== false,
  }

  await loadFormDataDeps()
  isSidebarOpen.value = true

  // Подгружаем детальные данные
  const detail = await ticketSettingsStore.fetchCategoryDetail(node.id)
  if (detail) {
    localName.value = detail.name || ''
    localDescription.value = detail.description || ''
    localParentId.value = detail.parent?.id ? String(detail.parent.id) : (detail.parent_id ? String(detail.parent_id) : null)
    localServiceId.value = detail.service?.id ? String(detail.service.id) : null
    localWorkGroupId.value = detail.work_group?.id || null
    localIsActive.value = detail.is_active !== false
    if (detail.responsible_employee) {
      localResponsible.value = { ...detail.responsible_employee }
    }

    initialFormState.value = {
      name: detail.name || '',
      description: detail.description || '',
      parent_id: localParentId.value,
      service_id: localServiceId.value,
      work_group_id: detail.work_group?.id || null,
      responsible_id: detail.responsible_employee?.id ? String(detail.responsible_employee.id) : null,
      is_active: detail.is_active !== false,
    }
  }
}

// Выбор ответственного сотрудника
const employeeSelectionModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

async function openResponsibleSelection() {
  if (!apiStore.employees?.length) {
    await apiStore.fetchAllEmployees()
  }
  employeeSelectionModalRef.value?.open()
}

function handleResponsibleSelected(emp: IEmployeeTreeItem) {
  if (!emp) return
  const { first_name, last_name, middle_name } = parseFullName(emp.full_name || '')
  localResponsible.value = {
    id: String(emp.id),
    full_name: emp.full_name || '',
    first_name: first_name || '',
    last_name: last_name || '',
    middle_name: middle_name || '',
    avatar: (emp as any).avatar || null,
    position_name: emp.position_name || null,
  }
}

function removeResponsible() {
  localResponsible.value = null
}

function getAvatarUrl(mgr: any): string {
  if (!mgr) return getRandomAvatar()
  if (mgr.avatar) return mgr.avatar
  return getRandomAvatar()
}

// Сохранение изменений
async function saveChanges() {
  if (!isCreateMode.value && selectedNode.value?.is_daomed_main) {
    try {
      await ticketSettingsStore.updateCategory(selectedNode.value.id, {
        is_active: localIsActive.value,
      })
      isSidebarOpen.value = false
      selectedNode.value = null
      showValidationErrors.value = false
    } catch (error) {
      // Ошибки уже обработаны в store
    }
    return
  }

  showValidationErrors.value = true
  if (!localName.value.trim()) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Укажите наименование категории',
      time: 4000,
    })
    return
  }

  if (!localServiceId.value) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Укажите ответственную службу (отделение)',
      time: 4000,
    })
    return
  }

  const payload = {
    name: localName.value.trim(),
    description: localDescription.value.trim(),
    parent_id: localParentId.value || null,
    service: { id: localServiceId.value },
    work_group: localWorkGroupId.value ? { id: localWorkGroupId.value } : null,
    responsible_employee: localResponsible.value?.id ? { id: String(localResponsible.value.id) } : null,
    is_active: localIsActive.value,
  }

  try {
    if (isCreateMode.value) {
      await ticketSettingsStore.createCategory(payload)
    } else if (selectedNode.value?.id) {
      await ticketSettingsStore.updateCategory(selectedNode.value.id, payload)
    }

    isSidebarOpen.value = false
    selectedNode.value = null
    showValidationErrors.value = false
  } catch (error) {
    // Ошибки уже обработаны в store
  }
}

function askDeleteCategory() {
  isConfirmDeleteOpen.value = true
}

async function confirmDelete(confirmed: boolean) {
  isConfirmDeleteOpen.value = false
  if (!confirmed || !selectedNode.value?.id) return

  try {
    await ticketSettingsStore.deleteCategory(selectedNode.value.id)
    isSidebarOpen.value = false
    selectedNode.value = null
  } catch (error) {
    // Ошибка обработана в сторе
  }
}

function closeModal() {
  emit('update:modelValue', false)
  isSidebarOpen.value = false
  selectedNode.value = null
}

function handleCloseModal() {
  if (isSidebarOpen.value && isChanged.value) {
    pendingAction.value = 'close_modal'
    isConfirmUnsavedChangesOpen.value = true
  } else {
    closeModal()
  }
}

function handleCloseSidebar() {
  if (isChanged.value) {
    pendingAction.value = 'close_sidebar'
    isConfirmUnsavedChangesOpen.value = true
  } else {
    isSidebarOpen.value = false
    selectedNode.value = null
  }
}

const onDismissAttempt = useDialogDismissGuard(handleCloseModal)

function onConfirmUnsavedClose(confirmed: boolean) {
  isConfirmUnsavedChangesOpen.value = false
  if (confirmed) {
    if (pendingAction.value === 'close_modal') {
      closeModal()
    } else if (pendingAction.value === 'close_sidebar') {
      isSidebarOpen.value = false
      selectedNode.value = null
    }
  }
  pendingAction.value = null
}
watch(() => props.modelValue, isOpen => {
  if (isOpen) {
    isCompactModeActive.value = localStorage.getItem('ticket_cat_tree_compact_mode') === 'true'
    isDragDropEnabled.value = localStorage.getItem('ticket_cat_tree_drag_drop') === 'true'
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    scrollable
    content-class="structure-modal-dialog"
    :scrim="smAndDown"
    persistent
    fullscreen
    :z-index="2400"
    @update:model-value="handleCloseModal"
    @click:outside="onDismissAttempt"
    @keydown.esc.stop="handleCloseModal"
  >
    <VCard class="structure-modal-card rounded-lg pa-0 d-flex flex-column position-relative">
      <!-- Шапка: Название и Закрытие (всегда вверху) -->
      <div class="d-flex align-center justify-space-between px-6 py-4 border-b">
        <span class="text-h4 font-weight-bold">{{ title }}</span>
        <VBtn
          icon="ri-close-line"
          variant="text"
          density="comfortable"
          @click="handleCloseModal"
        />
      </div>

      <!-- Панель фильтров и действий -->
      <div
        class="d-flex border-b gap-4 transition-all"
        :class="smAndDown ? 'flex-column align-stretch px-4 py-3' : 'align-center px-6 py-3 flex-wrap'"
      >
        <!-- Фильтр дерева: Все / Свои / Глобальные -->
        <div
          class="tree-filter-wrapper d-flex align-center gap-2"
          :data-onboarding="TICKETS_ANCHORS.categories.filter"
          :style="smAndDown ? 'width: 100%;' : 'max-width: 180px; min-width: 150px; width: 100%;'"
        >
          <VSelect
            v-model="treeFilterMode"
            :items="treeFilterOptions"
            item-title="label"
            item-value="value"
            label="Фильтр дерева"
            density="compact"
            variant="outlined"
            hide-details
            class="tree-filter-select"
            :menu-props="{ contentClass: 'entity-form-menu' }"
            @update:model-value="onTreeFilterChanged"
          />
        </div>

        <!-- Чекбокс Отображать компактно -->
        <VCheckbox
          v-model="isCompactModeActive"
          label="Отображать компактно"
          hide-details
          density="compact"
          class="compact-checkbox"
          :data-onboarding="TICKETS_ANCHORS.categories.view"
          :class="smAndDown ? 'w-100' : ''"
        />

        <!-- Чекбокс Скрывать иерархию -->
        <VCheckbox
          v-model="isHideHierarchyActive"
          label="Скрывать иерархию"
          hide-details
          density="compact"
          class="compact-checkbox"
          :class="smAndDown ? 'w-100' : ''"
        />

        <!-- Чекбокс Включить перетаскивание -->
        <div
          class="d-flex align-center ga-1 flex-shrink-0"
          :class="smAndDown ? 'w-100' : ''"
          :data-onboarding="TICKETS_ANCHORS.categories.viewLast"
        >
          <VCheckbox
            v-model="isDragDropEnabled"
            label="Включить перетаскивание"
            hide-details
            density="compact"
            class="compact-checkbox"
            :disabled="isGlobalTreeFilter"
          />
          <VTooltip
            v-if="isGlobalTreeFilter"
            location="top"
          >
            <template #activator="{ props: tipProps }">
              <span
                v-bind="tipProps"
                class="d-inline-flex"
              >
                <VIcon
                  icon="ri-information-line"
                  size="16"
                  color="secondary"
                />
              </span>
            </template>
            Перемещение глобальных категорий запрещено. Можно только менять их активность
          </VTooltip>
        </div>

        <VTextField
          v-model="searchQuery"
          placeholder="Поиск по категориям"
          prepend-inner-icon="ri-search-line"
          variant="underlined"
          density="compact"
          class="search-underlined"
          :class="smAndDown ? 'w-100' : 'ms-auto'"
          :style="smAndDown ? 'max-width: 100%;' : 'max-width: 320px; width: 100%;'"
          clearable
          hide-details
        />

        <VBtn
          v-if="!isGlobalTreeFilter"
          color="primary"
          prepend-icon="ri-add-line"
          height="36"
          class="text-none px-4"
          :class="smAndDown ? 'w-100 mt-2' : ''"
          :data-onboarding="TICKETS_ANCHORS.categories.create"
          @click="openCreateNode()"
        >
          Создать
        </VBtn>
      </div>

      <!-- Основная область с деревом -->
      <VCardText
        class="pa-0 flex-grow-1 overflow-hidden"
        :data-onboarding="TICKETS_ANCHORS.categories.tree"
      >
        <div class="d-flex h-100 align-stretch position-relative">
          <div class="pa-6 d-flex flex-column h-100 overflow-hidden flex-grow-1 position-relative">
            <!-- Дерево с прокруткой и панорамированием -->
            <div
              ref="treeContainerOuter"
              class="tree-container-outer w-100 h-100 flex-grow-1 overflow-auto"
              @mousedown="startDrag"
              @mousemove="onDrag"
              @mouseup="stopDrag"
              @mouseleave="stopDrag"
              @dragover.prevent
              @drop="onContainerDrop"
            >
              <div class="tree-container">
                <div class="tree d-inline-flex flex-column align-center mx-auto" :style="{ zoom: zoom / 100 }">
                  <ul v-if="ticketSettingsStore.categoriesTree && ticketSettingsStore.categoriesTree.length">
                    <TicketCategoryTreeNode
                      v-for="rootNode in ticketSettingsStore.categoriesTree"
                      :key="rootNode.id"
                      :node="rootNode"
                      :selected-node-id="isSidebarOpen && selectedNode ? String(selectedNode.id) : null"
                      :collapsed-nodes="collapsedNodes"
                      @select="selectNode"
                      @add-child="openCreateNode"
                      @toggle-collapse="toggleNodeCollapse"
                    />
                  </ul>
                  <div v-else-if="!feedbackStore.isGlobalLoading" class="text-center py-12 text-medium-emphasis">
                    <VIcon icon="ri-folder-settings-line" size="48" class="mb-3 opacity-40" />
                    <div class="text-h6 font-weight-medium">
                      {{ isGlobalTreeFilter ? 'Глобальных категорий нет' : 'Список категорий заявок пуст' }}
                    </div>
                    <div class="text-body-2 mb-4">
                      {{ isGlobalTreeFilter
                        ? 'Глобальные категории приходят из ДаоМед, создать их здесь нельзя'
                        : 'Создайте первую категорию заявки' }}
                    </div>
                    <VBtn
                      v-if="!isGlobalTreeFilter"
                      color="primary"
                      prepend-icon="ri-add-line"
                      @click="openCreateNode()"
                    >
                      Добавить категорию
                    </VBtn>
                  </div>
                </div>
              </div>
            </div>

            <!-- Панель масштабирования (слева снизу) -->
            <div class="zoom-control-panel d-flex align-center bg-surface border rounded px-1 py-0.5 shadow-sm">
              <VBtn
                icon="ri-add-line"
                variant="text"
                density="comfortable"
                :disabled="zoom >= 500"
                class="zoom-btn"
                @click="zoomIn"
              />
              <div class="d-flex align-center justify-center mx-1" style="min-width: 50px;">
                <input
                  v-model="zoomInput"
                  type="text"
                  class="zoom-input text-body-2 font-weight-bold text-center"
                  style="width: 32px; outline: none; border: none; background: transparent; text-align: center; color: inherit;"
                  @blur="onZoomInputBlur"
                  @keydown.enter="onZoomInputBlur"
                >
                <span class="text-body-2 font-weight-bold select-none" style="margin-left: -2px;">%</span>
              </div>
              <VBtn
                icon="ri-subtract-line"
                variant="text"
                density="comfortable"
                :disabled="zoom <= 10"
                class="zoom-btn"
                @click="zoomOut"
              />
            </div>
          </div>
        </div>
      </VCardText>

      <!-- Модальное окно формы создания/редактирования категории -->
      <VDialog
        :model-value="isSidebarOpen && selectedNode !== null"
        persistent
        width="1000"
        max-width="90vw"
        @click:outside="handleCloseSidebar"
        @keydown.esc.stop="handleCloseSidebar"
      >
        <VCard v-if="selectedNode" class="pa-0 rounded-lg d-flex flex-column" style="max-height: 90vh; overflow: hidden;">
          <!-- Заголовок модального окна -->
          <VCardTitle class="d-flex pa-6">
            <div class="form_card d-flex justify-space-between align-center w-100">
              <div class="form_card__title">
                <span class="text-h4 font-weight-bold">
                  {{ isCreateMode ? 'Создание категории заявки' : 'Редактирование категории заявки' }}
                </span>
              </div>
              <CustomCloseButton :close-action="handleCloseSidebar" />
            </div>
          </VCardTitle>

          <!-- Контент формы -->
          <VCardText class="overflow-y-auto flex-grow-1 pa-6">
            <VCol class="d-flex flex-column gap-6 px-0 py-0">
              <!-- Наименование категории -->
              <VTextField
                v-model="localName"
                placeholder="Введите наименование категории"
                variant="outlined"
                density="comfortable"
                persistent-placeholder
                :disabled="isDaomedMain"
                :error="showValidationErrors && !localName.trim()"
                :error-messages="showValidationErrors && !localName.trim() ? 'Укажите наименование' : ''"
                hide-details="auto"
              >
                <template #label>
                  <span>Наименование категории<sup class="text-error font-weight-bold ms-1">*</sup></span>
                </template>
              </VTextField>

              <!-- Описание -->
              <VTextarea
                v-model="localDescription"
                placeholder="Введите описание категории заявки"
                variant="outlined"
                density="comfortable"
                persistent-placeholder
                rows="3"
                auto-grow
                hide-details
                :disabled="isDaomedMain"
              >
                <template #label>
                  <span>Описание</span>
                </template>
              </VTextarea>

              <!-- Родительская категория -->
              <NewTreeComponent
                placeholder="Без родительской категории (корневая)"
                label="Родительская категория"
                search-placeholder="Поиск по категориям"
                :selected="{ value: localParentId ? { id: String(localParentId) } : null }"
                :tree-data="parentCategoryTree"
                :search-tree="searchParentCategoryTree"
                :disabled="isDaomedMain"
                @update:selected="(val) => localParentId = val ? String(val.id) : null"
              />

              <!-- Ответственная служба (отделение) -->
              <div>
                <NewTreeComponent
                  label="Ответственная служба"
                  :is-label-required="true"
                  placeholder="Выберите ответственную службу или отделение"
                  search-placeholder="Поиск по службам и отделениям"
                  :error="showValidationErrors && !localServiceId"
                  :selected="{ value: localServiceId ? { id: String(localServiceId) } : null }"
                  :tree-data="apiStore.departmentsTree"
                  :search-tree="searchDepartments"
                  :disabled="isDaomedMain"
                  @update:selected="(val) => localServiceId = val ? String(val.id) : null"
                />
                <div v-if="showValidationErrors && !localServiceId" class="text-caption text-error mt-1 ms-1">
                  Укажите ответственную службу
                </div>
              </div>

              <!-- Группа исполнителей через SearchableSelectList -->
              <VMenu
                v-model="showWorkGroupMenu"
                :close-on-content-click="false"
                :max-height="350"
                :disabled="isDaomedMain"
                location="bottom"
                transition="scale-transition"
                content-class="entity-form-menu tree-select-menu"
              >
                <template #activator="{ props: menuProps }">
                  <VTextField
                    :model-value="selectedWorkGroupName"
                    class="invisible-readonly"
                    :class="{ 'cursor-pointer': !isDaomedMain }"
                    :append-inner-icon="showWorkGroupMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                    variant="outlined"
                    density="comfortable"
                    placeholder="Выберите группу исполнителей"
                    persistent-placeholder
                    readonly
                    :clearable="!isDaomedMain"
                    :disabled="isDaomedMain"
                    v-bind="menuProps"
                    @click:append-inner.stop="!isDaomedMain && (showWorkGroupMenu = !showWorkGroupMenu)"
                    @click:clear="onClearWorkGroup"
                  >
                    <template #label>
                      <span>Группа исполнителей</span>
                    </template>

                    <!-- Участники в инпуте при выбранной группе -->
                    <template v-if="selectedWorkGroupPerformers.length" #append-inner>
                      <div class="d-flex align-center flex-shrink-0 text-no-wrap me-1">
                        <VMenu
                          open-on-hover
                          :close-on-content-click="false"
                          close-delay="200"
                          location="bottom end"
                          offset="8"
                        >
                          <template #activator="{ props: popoverProps }">
                            <span
                              v-bind="popoverProps"
                              class="text-body-2 text-primary font-weight-medium cursor-pointer text-no-wrap flex-shrink-0"
                              @click.stop
                            >
                              Участники: {{ selectedWorkGroupPerformers.length }}
                            </span>
                          </template>

                          <CustomSelectedItems
                            :items="selectedWorkGroupPerformers"
                            :title="`Участники ${selectedWorkGroupPerformers.length}:`"
                            readonly
                          />
                        </VMenu>
                      </div>
                    </template>
                  </VTextField>
                </template>

                <SearchableSelectList
                  :items="workGroupSelectItems"
                  placeholder="Поиск группы исполнителей..."
                  server-search
                  :loading="feedbackStore.isGlobalLoading"
                  :has-more="!!ticketSettingsStore.workGroupsNextCursor"
                  @select="onSelectWorkGroup"
                  @search="onSearchWorkGroups"
                  @load-more="onLoadMoreWorkGroups"
                >
                  <template #item="{ item, highlightMatch }">
                    <div class="d-flex align-center justify-space-between w-100 py-1">
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <span
                        class="text-body-1 text-high-emphasis text-wrap"
                        v-html="highlightMatch(item.label)"
                      />

                      <VMenu
                        v-if="item.formattedPerformers?.length"
                        open-on-hover
                        :close-on-content-click="false"
                        close-delay="200"
                        location="end top"
                        offset="8"
                      >
                        <template #activator="{ props: popoverProps }">
                          <span
                            v-bind="popoverProps"
                            class="text-body-2 text-primary font-weight-medium cursor-pointer d-inline-block ms-2 flex-shrink-0"
                            @click.stop
                          >
                            Участники: {{ item.performersCount }}
                          </span>
                        </template>

                        <CustomSelectedItems
                          :items="item.formattedPerformers"
                          :title="`Участники ${item.performersCount}:`"
                          readonly
                        />
                      </VMenu>
                    </div>
                  </template>
                </SearchableSelectList>
              </VMenu>

              <!-- Ответственный сотрудник -->
              <div class="d-flex flex-column gap-3">
                <div class="d-flex align-center justify-space-between w-100">
                  <span class="text-uppercase text-caption text-medium-emphasis font-weight-medium" style="letter-spacing: 0.8px; opacity: 0.8;">
                    Ответственный сотрудник
                  </span>
                  <div v-if="!isDaomedMain" class="d-flex align-center gap-1">
                    <VTooltip v-if="!localResponsible" location="top" text="Назначить ответственного">
                      <template #activator="{ props: tooltipProps }">
                        <VIconBtn
                          v-bind="tooltipProps"
                          size="small"
                          variant="text"
                          color="primary"
                          @click="openResponsibleSelection"
                        >
                          <VIcon icon="ri-add-line" size="20" />
                        </VIconBtn>
                      </template>
                    </VTooltip>

                    <template v-else>
                      <VTooltip location="top" text="Сменить ответственного">
                        <template #activator="{ props: tooltipProps }">
                          <VIconBtn
                            v-bind="tooltipProps"
                            size="small"
                            variant="text"
                            color="primary"
                            @click="openResponsibleSelection"
                          >
                            <VIcon icon="ri-pencil-line" size="20" />
                          </VIconBtn>
                        </template>
                      </VTooltip>

                      <VTooltip location="top" text="Удалить ответственного">
                        <template #activator="{ props: tooltipProps }">
                          <VIconBtn
                            v-bind="tooltipProps"
                            size="small"
                            variant="text"
                            color="error"
                            @click="removeResponsible"
                          >
                            <VIcon icon="ri-delete-bin-line" size="20" />
                          </VIconBtn>
                        </template>
                      </VTooltip>
                    </template>
                  </div>
                </div>

                <div v-if="localResponsible" class="d-flex align-center gap-3 employee-item">
                  <VAvatar size="40" class="border" :image="getAvatarUrl(localResponsible)" />
                  <div class="d-flex flex-column min-width-0">
                    <span class="text-body-1 font-weight-bold text-high-emphasis text-truncate">
                      {{ localResponsible.full_name || [localResponsible.last_name, localResponsible.first_name, localResponsible.middle_name].filter(Boolean).join(' ') }}
                    </span>
                    <span v-if="localResponsible.position_name" class="text-caption text-medium-emphasis text-truncate">
                      {{ localResponsible.position_name }}
                    </span>
                  </div>
                </div>
                <div v-else>
                  <VChip
                    color="secondary"
                    size="small"
                    variant="tonal"
                    class="rounded"
                  >
                    Не назначен
                  </VChip>
                </div>
              </div>

              <!-- Переключатель активности -->
              <div class="d-flex align-center justify-space-between pt-2">
                <div class="d-flex flex-column">
                  <span class="text-body-1 font-weight-medium">Активная категория</span>
                  <span class="text-caption text-medium-emphasis">
                    Отображается в формах создания и фильтрах заявок
                  </span>
                </div>
                <VSwitch v-model="localIsActive" color="success" hide-details density="compact" />
              </div>
            </VCol>
          </VCardText>

          <!-- Подвал модалки формы -->
          <div
            class="border-t px-6 py-4 bg-surface mt-auto d-flex"
            :class="smAndDown ? 'flex-column gap-6' : 'flex-row align-center justify-space-between gap-4'"
          >
            <div>
              <VBtn
                v-if="!isCreateMode && !isDaomedMain"
                color="error"
                variant="outlined"
                prepend-icon="ri-delete-bin-line"
                @click="askDeleteCategory"
              >
                Удалить
              </VBtn>
            </div>

            <VBtn
              color="primary"
              :class="{ 'ms-auto': !smAndDown }"
              :disabled="!isChanged"
              @click="saveChanges"
            >
              {{ isCreateMode ? 'Создать' : 'Сохранить' }}
            </VBtn>
          </div>
        </VCard>
      </VDialog>

      <!-- Диалог подтверждения несохраненных изменений -->
      <ConfirmDialog
        v-model:is-dialog-visible="isConfirmUnsavedChangesOpen"
        confirmation-question="Закрыть без сохранения?"
        confirmation-text="У вас есть несохраненные изменения. Вы действительно хотите закрыть окно без сохранения?"
        confirm-btn="Закрыть"
        reject-btn="Отмена"
        @confirm="onConfirmUnsavedClose"
      />

      <!-- Диалог подтверждения удаления -->
      <ConfirmDialog
        v-model:is-dialog-visible="isConfirmDeleteOpen"
        :confirmation-question="`Удалить категорию «${selectedNode?.name || localName}»?`"
        :confirmation-text="`Вы уверены, что хотите удалить «${selectedNode?.name || localName}»? Это действие нельзя отменить.`"
        confirm-btn="Удалить"
        reject-btn="Отмена"
        @confirm="confirmDelete"
      />

      <!-- Модальное окно выбора ответственного сотрудника -->
      <EmployeeSelectionModal
        ref="employeeSelectionModalRef"
        :employees="apiStore.employees"
        @select="handleResponsibleSelected"
      />
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.structure-modal-card {
  height: 100% !important;
  max-height: 100% !important;
}

.transition-all {
  transition: all 0.25s ease-in-out;
}

.bg-light {
  background-color: rgba(var(--v-theme-on-surface), 0.02) !important;
}

:deep(.search-underlined.v-text-field) {
  .v-field__control {
    height: 36px !important;
  }
  .v-field__prepend-inner {
    align-self: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .v-field__input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 36px !important;
    align-self: center !important;
    display: flex !important;
    align-items: center !important;
  }
}

.tree-container-outer {
  &.cursor-grab {
    cursor: grab;
  }

  &.cursor-grabbing {
    cursor: grabbing;
    user-select: none;
  }

  &.is-dragging-process {
    :deep(.dept-node-card) * {
      pointer-events: none !important;
    }
  }
}

.tree-container {
  min-width: 100%;
  width: max-content;
  display: flex;
  justify-content: safe center;
  padding: 40px 80px;
}

.tree :deep(ul) {
  padding-top: 32px;
  position: relative;
  transition: all 0.5s;
  display: flex;
  justify-content: center;
  gap: 60px;
  padding-left: 0;
  margin: 0;
  width: max-content;
  min-width: 100%;
}

.tree :deep(li) {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-top: 32px;
  list-style-type: none;
  flex-shrink: 0;
  width: min-content;
}

.tree :deep(ul:has(> .has-subdepartments ~ .has-subdepartments)) {
  & > li {
    width: auto;
  }
  & > li > ul {
    width: auto;
    min-width: 0;
  }
}

/* Линии */
.tree :deep(li::before), .tree :deep(li::after) {
  content: '';
  position: absolute;
  top: 0;
  right: 50%;
  border-top: 2px solid rgb(var(--v-theme-primary));
  width: calc(50% + 30px);
  height: 32px;
  z-index: 1;
}

.tree :deep(li::after) {
  right: auto;
  left: 50%;
  border-left: 2px solid rgb(var(--v-theme-primary));
}

.tree :deep(li:first-child::after) {
  border-radius: 16px 0 0 0;
}

.tree :deep(li:last-child::before) {
  border-right: 2px solid rgb(var(--v-theme-primary));
  border-radius: 0 16px 0 0;
  border-left: none;
}

.tree :deep(li:only-child::after), .tree :deep(li:only-child::before) {
  display: none;
}

.tree :deep(li:only-child) {
  padding-top: 0;
}

.tree :deep(li:first-child::before), .tree :deep(li:last-child::after) {
  border: 0 none;
}

.tree :deep(ul::before) {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  border-left: 2px solid rgb(var(--v-theme-primary));
  width: 0;
  height: 32px;
  z-index: 1;
}

.tree > ul::before {
  display: none !important;
}

.tree > ul > li::before,
.tree > ul > li::after {
  display: none !important;
}

.tree > ul > li {
  padding-top: 0 !important;
}

.tree :deep(.tree-node-wrapper) {
  position: relative;
  z-index: 2;
}

.tree :deep(.tree-node-wrapper::after) {
  content: '';
  position: absolute;
  bottom: -32px;
  left: 50%;
  border-left: 2px solid rgb(var(--v-theme-primary));
  width: 0;
  height: 32px;
  z-index: 1;
  display: none;
}

.tree :deep(.tree-node-wrapper.has-children::after) {
  display: block;
}

/* Карточки */
.tree :deep(.dept-node-card) {
  width: 380px;
  height: 312px;
  background: rgb(var(--v-theme-surface));
  border-radius: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.25s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.tree :deep(.dept-node-card.compact) {
  height: auto !important;
}

.tree :deep(.dept-node-card.compact .bottom-action-btn) {
  margin-top: 16px !important;
}

.tree :deep(.dept-node-card:hover) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(var(--v-theme-primary), 0.15);
  border-color: rgb(var(--v-theme-primary)) !important;
}

.tree :deep(.dept-node-card.selected) {
  border-color: rgb(var(--v-theme-primary-300)) !important;
  box-shadow: 0 0 0 8px rgb(var(--v-theme-primary-300)), 0 4px 12px rgba(46, 38, 61, 0.2) !important;
}

.tree :deep(li .dept-node-card::before) {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0 6px;
  border-style: solid;
  border-color: rgb(var(--v-theme-primary)) transparent transparent transparent;
  width: 0;
  height: 0;
  z-index: 3;
}

.tree :deep(.bottom-action-btn) {
  margin: 0 16px 16px 16px;
  border-radius: 6px;
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
  color: rgb(var(--v-theme-primary)) !important;
  transition: all 0.2s ease;
  cursor: pointer;
  margin-top: auto;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tree :deep(.bottom-action-btn:hover:not(.disabled)) {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}

.tree :deep(.bottom-action-btn.disabled) {
  margin: 0 16px 16px 16px;
  border-radius: 6px;
  background-color: rgba(var(--v-theme-on-surface), 0.03);
  color: rgba(var(--v-theme-on-surface), 0.4);
  cursor: default;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-control-panel {
  position: absolute;
  left: 24px;
  bottom: 48px;
  z-index: 10;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  background-color: rgb(var(--v-theme-surface)) !important;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;

  .zoom-btn {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    padding: 0 !important;
    color: rgba(var(--v-theme-on-surface), 0.6) !important;

    &:hover {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}

.tree :deep(.tree-node-wrapper::after),
.tree :deep(.tree-node-wrapper.has-children::after) {
  display: none !important;
}

.tree :deep(li.has-subdepartments) {
  align-items: flex-start !important;
}

.tree :deep(ul ul) {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  padding-left: 50px !important;
  padding-top: 32px !important;
  gap: 24px !important;
  position: relative !important;
  width: max-content !important;
  margin: 0 !important;

  &::before {
    display: none !important;
  }
}

.tree :deep(ul ul::after) {
  display: none !important;
}

.tree :deep(ul ul > li) {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  padding-top: 0 !important;
  padding-left: 0 !important;
  width: max-content !important;
  position: relative !important;
}

.tree :deep(ul ul .tree-node-wrapper) {
  margin-left: 0 !important;
  margin-right: 0 !important;
  position: relative !important;
}

.tree :deep(ul ul > li::before) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important;
  top: 0 !important;
  bottom: -24px !important;
  width: 0 !important;
  height: auto !important;
  border-left: 2px solid rgb(var(--v-theme-primary)) !important;
  border-top: none !important;
  border-right: none !important;
  border-bottom: none !important;
  border-radius: 0 !important;
  z-index: 1 !important;
  display: block !important;
}

.tree :deep(ul ul > li:first-child::before) {
  top: -32px !important;
}

.tree :deep(ul ul > li:last-child::before) {
  display: none !important;
}

.tree :deep(ul ul .tree-node-wrapper::after),
.tree :deep(ul ul .tree-node-wrapper.has-children::after) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important;
  width: 30px !important;
  top: 50% !important;
  height: 0 !important;
  border-top: 2px solid rgb(var(--v-theme-primary)) !important;
  transform: translateY(-50%) !important;
  z-index: 1 !important;
  display: block !important;
}

.tree :deep(ul ul > li:last-child .tree-node-wrapper::before) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important;
  top: 0 !important;
  height: 50% !important;
  width: 0 !important;
  border-left: 2px solid rgb(var(--v-theme-primary)) !important;
  z-index: 1 !important;
  display: block !important;
}

.tree :deep(ul ul > li:only-child .tree-node-wrapper::before) {
  content: '' !important;
  position: absolute !important;
  left: -30px !important;
  top: -32px !important;
  height: calc(50% + 32px) !important;
  width: 0 !important;
  border-left: 2px solid rgb(var(--v-theme-primary)) !important;
  z-index: 1 !important;
  display: block !important;
}

.tree :deep(ul ul > li::after) {
  display: none !important;
}

/* Приглушённые линии и рамка глобальных узлов */
.tree :deep(li.is-daomed-locked-branch::before),
.tree :deep(li.is-daomed-locked-branch::after),
.tree :deep(li.is-daomed-locked-branch > .tree-node-wrapper::before),
.tree :deep(li.is-daomed-locked-branch > .tree-node-wrapper::after),
.tree :deep(li.is-daomed-locked-branch > .tree-node-wrapper.has-children::after),
.tree :deep(li.is-daomed-locked-branch > ul::before),
.tree :deep(ul ul > li.is-daomed-locked-branch::before),
.tree :deep(ul ul > li.is-daomed-locked-branch .tree-node-wrapper::before),
.tree :deep(ul ul > li.is-daomed-locked-branch .tree-node-wrapper::after),
.tree :deep(ul ul > li.is-daomed-locked-branch .tree-node-wrapper.has-children::after),
.tree :deep(ul ul > li.is-daomed-locked-branch:last-child .tree-node-wrapper::before),
.tree :deep(ul ul > li.is-daomed-locked-branch:only-child .tree-node-wrapper::before) {
  border-color: rgba(var(--v-theme-on-surface), 0.2) !important;
  border-left-color: rgba(var(--v-theme-on-surface), 0.2) !important;
  border-top-color: rgba(var(--v-theme-on-surface), 0.2) !important;
  border-right-color: rgba(var(--v-theme-on-surface), 0.2) !important;
}

.tree :deep(li.is-daomed-locked-branch .dept-node-card::before) {
  border-color: rgba(var(--v-theme-on-surface), 0.2) transparent transparent transparent !important;
}

.tree :deep(.dept-node-card.is-daomed-locked),
.tree :deep(.dept-node-card.is-daomed-locked:hover),
.tree :deep(.dept-node-card.is-daomed-locked.selected) {
  border-color: rgba(var(--v-theme-on-surface), 0.16) !important;
}

.employee-item {
  padding: 8px 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 6px;
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}
</style>
